import { RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'

import { describeDays, formatDate } from '@/domain/dates'
import type { DerivedItem } from '@/domain/types'
import { KIND_LABELS } from '@/domain/types'
import { formatMinor } from '@/domain/money'
import { Badge } from './ui'

export function ItemCard({ derived }: { derived: DerivedItem }) {
  const { item, actionBy, expiryDate, daysRemaining, urgency, monthlyMinor } = derived
  const hasNotice = item.notice_period_days > 0

  return (
    <Link
      to={`/items/${item.id}`}
      className="block rounded-xl border border-line bg-surface p-4 transition-colors hover:border-brand"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-ink">{item.name}</span>
            <Badge>{KIND_LABELS[item.kind]}</Badge>
            {item.status !== 'active' && <Badge>{item.status}</Badge>}
          </div>

          {item.provider && (
            <p className="mt-0.5 truncate text-sm text-ink-muted">{item.provider}</p>
          )}

          <p className="mt-2 text-sm text-ink">
            {hasNotice ? 'Act by' : 'Expires'} {formatDate(actionBy)}
          </p>

          {/* Only shown when it differs, otherwise it reads as a duplicate. */}
          {hasNotice && (
            <p className="mt-0.5 text-xs text-ink-muted">
              Expires {formatDate(expiryDate)} · {item.notice_period_days} days' notice required
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
            {monthlyMinor > 0 && <span>{formatMinor(monthlyMinor, item.currency)}/month</span>}
            {item.auto_renews && (
              <span className="inline-flex items-center gap-1">
                <RefreshCw className="size-3" aria-hidden="true" />
                Auto-renews
              </span>
            )}
          </div>
        </div>

        <Badge urgency={urgency} className="shrink-0">
          {describeDays(daysRemaining)}
        </Badge>
      </div>
    </Link>
  )
}
